/**
 * Loads FHIR Coverage and Stripe payment methods for a patient (tracker drawer / billing preview).
 */

import { useMedplum } from '@medplum/react-hooks';
import { useEffect, useState } from 'react';
import type { Coverage } from '@medplum/fhirtypes';
import { billingApi } from '../api/billing';
import type { SavedPaymentMethod } from '../lib/stripeBillingService';

export interface UsePatientCoverageAndPaymentResult {
  coverages: Coverage[];
  paymentMethods: SavedPaymentMethod[];
  loading: boolean;
  error: string | null;
}

export function usePatientCoverageAndPayment(
  patientId: string | undefined,
): UsePatientCoverageAndPaymentResult {
  const medplum = useMedplum();
  const [coverages, setCoverages] = useState<Coverage[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<SavedPaymentMethod[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!patientId) {
      setCoverages([]);
      setPaymentMethods([]);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    void (async () => {
      const patientRef = `Patient/${patientId}`;
      let cov: Coverage[] = [];
      let covError: string | null = null;
      try {
        cov = await medplum.searchResources('Coverage', {
          patient: patientRef,
          _sort: '-_lastUpdated',
          _count: '20',
        });
      } catch (e) {
        covError = e instanceof Error ? e.message : 'Coverage search failed';
      }

      const pm = await billingApi.listPaymentMethods(true, patientId).catch(() => [] as SavedPaymentMethod[]);

      if (!cancelled) {
        setCoverages(cov);
        setPaymentMethods(pm);
        setError(covError);
      }
      if (!cancelled) setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [medplum, patientId]);

  return { coverages, paymentMethods, loading, error };
}
